import { Plane, Clock, X, Calendar, Radio, Compass, Wind } from 'lucide-react';
import {
  IATA_CITIES,
  AIRCRAFT_NAMES,
  format24HourTime,
  formatDate
} from '../../../utils/trackingHelpers';
import { FLIGHT_STATUS_CONFIG } from '../../../../../data/tracking/trackingData';

const FlightTrackingResults = ({ flightData, onClose }) => {
  if (!flightData) return null;

  // Strict 24-hour time format per workspace rules
  const formatTime = (isoString) => {
    if (!isoString) return '--:--';
    return format24HourTime(isoString) || '--:--';
  };

  const getBestTime = (primary, secondary, fallback) => {
    if (primary && typeof primary === 'string' && primary.trim() !== '') return primary;
    if (secondary && typeof secondary === 'string' && secondary.trim() !== '') return secondary;
    return fallback;
  };

  const getStatusConfig = (status) => {
    const s = String(status || '').toLowerCase();
    if (FLIGHT_STATUS_CONFIG[s]) {
      return FLIGHT_STATUS_CONFIG[s];
    }
    return {
      label: status ? status.toUpperCase() : 'Scheduled',
      flightPhase: 'Flight in Transit',
      badgeClass: 'bg-amber-50 text-amber-700 border-amber-200/80',
      dotClass: 'bg-amber-500',
      pulse: false
    };
  };

  const calculateProgress = () => {
    const status = (flightData.flight_status || flightData.flight?.status || '').toLowerCase();
    if (status === 'landed') return 100;
    if (status === 'cancelled') return 0;
    if (status === 'scheduled') return 0;

    try {
      const depTimeString = getBestTime(
        flightData.departure?.actual,
        flightData.departure?.estimated,
        flightData.departure?.scheduled
      );
      const arrTimeString = getBestTime(
        flightData.arrival?.actual,
        flightData.arrival?.estimated,
        flightData.arrival?.scheduled
      );

      if (!depTimeString || !arrTimeString) return status === 'active' ? 50 : 0;

      const dep = new Date(depTimeString).getTime();
      const arr = new Date(arrTimeString).getTime();
      const now = Date.now();

      if (now < dep) return 0;
      if (now > arr) return 100;

      const total = arr - dep;
      const elapsed = now - dep;
      return Math.min(Math.max((elapsed / total) * 100, 5), 95);
    } catch (e) {
      return status === 'active' ? 50 : 0;
    }
  };

  const getDelayInfo = (type) => {
    const data = flightData?.[type];
    if (!data || !data.scheduled) return { delay: 0, text: 'On Time' };
    try {
      const sched = new Date(data.scheduled).getTime();
      const best = new Date(getBestTime(data.actual, data.estimated, data.scheduled)).getTime();
      const diffMin = Math.round((best - sched) / (1000 * 60));
      return diffMin > 1 ? { delay: diffMin, text: `${diffMin}m Delay` } : { delay: 0, text: 'On Time' };
    } catch (e) {
      return { delay: 0, text: 'On Time' };
    }
  };

  const getCity = (iata, airportName) => {
    if (iata && IATA_CITIES[iata.toUpperCase()]) {
      return IATA_CITIES[iata.toUpperCase()];
    }
    if (airportName) {
      return airportName.replace(/\s*(International|Int'l|Airport|Domestic|Regional|Sahar).*$/i, '').trim() || airportName;
    }
    return iata || 'City';
  };

  const getFlightDuration = (depTime, arrTime) => {
    if (!depTime || !arrTime) return null;
    try {
      const dep = new Date(depTime).getTime();
      const arr = new Date(arrTime).getTime();
      let diffMs = arr - dep;
      if (diffMs < 0) diffMs += 24 * 60 * 60 * 1000;
      const diffMins = Math.round(diffMs / (1000 * 60));
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      if (hours > 0) {
        return `${hours}h ${mins > 0 ? `${mins}m` : ''}`.trim();
      }
      return `${mins}m`;
    } catch (e) {
      return null;
    }
  };

  const getAircraftName = (aircraft) => {
    if (!aircraft) return 'Commercial Aircraft';
    const code = (aircraft.iata || aircraft.icao || '').toUpperCase();
    if (!code) return 'Commercial Aircraft';
    for (const [key, name] of Object.entries(AIRCRAFT_NAMES)) {
      if (code.includes(key)) return name;
    }
    return code;
  };

  const depIata = flightData.departure?.iata || 'DEP';
  const arrIata = flightData.arrival?.iata || 'ARR';
  const depCity = getCity(depIata, flightData.departure?.airport);
  const arrCity = getCity(arrIata, flightData.arrival?.airport);
  const depAirport = flightData.departure?.airport || 'Origin Airport';
  const arrAirport = flightData.arrival?.airport || 'Destination Airport';
  const depTime = formatTime(flightData.departure?.scheduled);
  const arrTime = formatTime(flightData.arrival?.scheduled);
  const depDelay = getDelayInfo('departure');
  const arrDelay = getDelayInfo('arrival');
  const statusConfig = getStatusConfig(flightData.flight_status || flightData.flight?.status);
  const progress = calculateProgress();
  const duration = getFlightDuration(flightData.departure?.scheduled, flightData.arrival?.scheduled);

  const airlineName = flightData.airline?.name || 'Airline';
  const flightNumber = flightData.flight?.iata || flightData.flight?.number || 'Flight';
  const aircraftName = getAircraftName(flightData.aircraft);
  const aircraftReg = flightData.aircraft?.registration || null;

  // Mathematical calculations for the Great Circle flight trajectory arc
  const t = Math.min(Math.max(progress / 100, 0), 1);
  const arcP0 = { x: 25, y: 72 };
  const arcP1 = { x: 180, y: 8 };
  const arcP2 = { x: 335, y: 72 };

  const planeX = (1 - t) * (1 - t) * arcP0.x + 2 * (1 - t) * t * arcP1.x + t * t * arcP2.x;
  const planeY = (1 - t) * (1 - t) * arcP0.y + 2 * (1 - t) * t * arcP1.y + t * t * arcP2.y;

  const tangentDx = 2 * (1 - t) * (arcP1.x - arcP0.x) + 2 * t * (arcP2.x - arcP1.x);
  const tangentDy = 2 * (1 - t) * (arcP1.y - arcP0.y) + 2 * t * (arcP2.y - arcP1.y);
  const tangentAngle = (Math.atan2(tangentDy, tangentDx) * 180) / Math.PI;

  const totalArcLength = 330;
  const contrailOffset = totalArcLength * (1 - t);

  return (
    <div className="w-full bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-7 border border-slate-200/80 shadow-sm transition-all duration-300 relative overflow-hidden animate-fade-in-up">
      {/* Top Subtle Brand Gradient Line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#067978] via-teal-400 to-[#067978]" />

      {/* Subtle Boarding Pass Cutout Ticket Notches */}
      <div className="hidden sm:block absolute left-0 top-[230px] w-3 h-6 bg-slate-50 border-r border-y border-slate-200/80 rounded-r-full -translate-x-px z-10" />
      <div className="hidden sm:block absolute right-0 top-[230px] w-3 h-6 bg-slate-50 border-l border-y border-slate-200/80 rounded-l-full translate-x-px z-10" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 pb-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#eaf8f7] text-[#067978] border border-[#c2ecea]/70 flex items-center justify-center shrink-0 shadow-2xs">
            <Plane size={18} className="rotate-45 text-[#067978]" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg sm:text-xl font-normal text-slate-800 tracking-tight">
                {airlineName} <span className="text-slate-500">{flightData.flight?.number || ''}</span>
              </h2>
              <span className="text-[11px] font-mono font-normal px-2 py-0.5 rounded-md bg-slate-50 text-slate-600 border border-slate-200/60 uppercase">
                #{flightNumber}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-0.5 text-xs text-slate-400 font-normal">
              <span className="font-mono">{flightData.flight?.iata || flightData.flight?.icao}</span>
              <span>·</span>
              <span className="flex items-center gap-1">
                <Calendar size={12} className="text-slate-400" />
                {formatDate(flightData.flight_date)}
              </span>
              {flightData.flight?.codeshared?.airline_name && (
                <>
                  <span>·</span>
                  <span className="text-teal-700 bg-teal-50/90 border border-teal-200/60 px-1.5 py-0.5 rounded text-[11px] font-medium">
                    Operated by {flightData.flight.codeshared.airline_name.toUpperCase()} {flightData.flight.codeshared.flight_iata ? `(${flightData.flight.codeshared.flight_iata.toUpperCase()})` : ''}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right Side: Status Badge + Close Button */}
        <div className="flex items-center gap-2 self-start sm:self-center">
          <div
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-normal border shadow-2xs ${statusConfig.badgeClass}`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${statusConfig.dotClass} ${statusConfig.pulse ? 'animate-ping' : ''}`}
            />
            <span>{statusConfig.label}</span>
          </div>

          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer"
              title="Close tracking"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Aviation Sky Corridor & Great Circle Flight Arc */}
      <div className="bg-gradient-to-b from-sky-50/40 via-teal-50/20 to-slate-50/50 rounded-2xl p-4 sm:p-6 border border-slate-200/70 my-5 relative overflow-hidden">
        {/* Upper Status & Flight Stage Strip */}
        <div className="flex flex-wrap items-center justify-between gap-2 pb-3 mb-2 border-b border-slate-200/50 text-[11px] text-slate-500 font-normal">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white text-slate-600 border border-slate-200/60 shadow-2xs">
              <Clock size={11} className="text-[#067978]" />
              <span>{duration ? `${duration} · Non-Stop Flight` : 'Direct Flight'}</span>
            </span>
            <span className="hidden sm:inline-block text-slate-300">·</span>
            <span className="hidden sm:inline-block text-slate-500 font-normal">{statusConfig.flightPhase}</span>
          </div>

          <div className="flex items-center gap-2 font-mono text-slate-400 text-[10px]">
            <span className="flex items-center gap-1">
              <Compass size={11} className="text-[#067978]" />
              <span>
                HDG {flightData.live?.direction ? `${Math.round(flightData.live.direction)}°` : '082° ENE'}
              </span>
            </span>
            <span>·</span>
            <span className="flex items-center gap-1">
              <Wind size={11} className="text-[#067978]" />
              <span>
                {flightData.live?.speed_horizontal
                  ? `${Math.round(flightData.live.speed_horizontal)} km/h`
                  : '460 kts'}
              </span>
            </span>
          </div>
        </div>

        {/* Route Visualizer */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-center my-2">
          {/* Departure Node */}
          <div className="md:col-span-3 flex flex-col items-start text-left">
            <span className="text-[10px] uppercase font-medium text-slate-400 tracking-wider block mb-1">
              Departure Airport
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-normal text-slate-800 font-mono tracking-tight">{depIata}</span>
              <span className="text-sm font-normal text-slate-600 truncate max-w-[130px]">{depCity}</span>
            </div>
            <p className="text-xs text-slate-400 font-normal truncate max-w-[200px] mt-0.5" title={depAirport}>
              {depAirport}
            </p>

            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-xl font-normal text-slate-800 font-mono">{depTime}</span>
              {depDelay.delay > 0 ? (
                <span className="text-[11px] font-normal text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200/60">
                  +{depDelay.delay}m delay
                </span>
              ) : (
                <span className="text-[11px] font-normal text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200/60">
                  On Time
                </span>
              )}
            </div>

            <span className="text-[11px] text-slate-500 font-normal mt-1">
              {flightData.departure?.terminal ? `Terminal ${flightData.departure.terminal}` : 'Terminal TBA'}
              {flightData.departure?.gate ? ` · Gate ${flightData.departure.gate}` : ''}
            </span>
          </div>

          {/* Central Great Circle Sky Arc */}
          <div className="md:col-span-6 flex flex-col items-center justify-center px-1">
            <div className="w-full relative flex items-center justify-center">
              <svg viewBox="0 0 360 90" className="w-full h-24 overflow-visible">
                <defs>
                  <linearGradient id="contrailGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#067978" stopOpacity="0.4" />
                    <stop offset="50%" stopColor="#067978" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#14b8a6" stopOpacity="1" />
                  </linearGradient>

                  <radialGradient id="skyGlow" cx="50%" cy="30%" r="50%">
                    <stop offset="0%" stopColor="#ccfbf1" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.35" />
                  </radialGradient>
                </defs>

                <rect x="0" y="0" width="360" height="90" fill="url(#skyGlow)" rx="12" />

                <g opacity="0.4">
                  <path
                    d="M 50 45 a 8 8 0 0 1 12 -2 a 12 12 0 0 1 18 2 a 8 8 0 0 1 8 8 l -38 0 z"
                    fill="#e2e8f0"
                  />
                  <path
                    d="M 260 35 a 7 7 0 0 1 12 -2 a 11 11 0 0 1 16 2 a 7 7 0 0 1 7 7 l -35 0 z"
                    fill="#e2e8f0"
                  />
                </g>

                <line
                  x1="120"
                  y1="16"
                  x2="240"
                  y2="16"
                  stroke="#067978"
                  strokeOpacity="0.18"
                  strokeDasharray="2 3"
                  strokeWidth="1"
                />
                <text
                  x="180"
                  y="12"
                  textAnchor="middle"
                  fill="#94a3b8"
                  fontSize="7.5"
                  fontFamily="monospace"
                  letterSpacing="0.8"
                >
                  CRUISE FL340 · TROPOSPHERE
                </text>

                <path
                  d="M 25 72 Q 180 8 335 72"
                  fill="none"
                  stroke="#cbd5e1"
                  strokeWidth="1.5"
                  strokeDasharray="4 4"
                  strokeOpacity="0.85"
                />

                <path
                  d="M 25 72 Q 180 8 335 72"
                  fill="none"
                  stroke="url(#contrailGrad)"
                  strokeWidth="2.5"
                  strokeDasharray={totalArcLength}
                  strokeDashoffset={contrailOffset}
                  strokeLinecap="round"
                />

                <path
                  d="M 25 72 Q 180 8 335 72"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="1"
                  strokeDasharray="2 6"
                  strokeDashoffset={contrailOffset}
                  strokeOpacity="0.75"
                />

                <circle cx={arcP0.x} cy={arcP0.y} r="3" fill="#ffffff" stroke="#067978" strokeWidth="2" />
                <circle cx={arcP2.x} cy={arcP2.y} r="3" fill="#ffffff" stroke="#10b981" strokeWidth="2" />

                <g transform={`translate(${planeX}, ${planeY})`}>
                  {statusConfig.pulse && (
                    <circle r="12" fill="none" stroke="#067978" strokeWidth="1" opacity="0.4" className="animate-ping" />
                  )}

                  <ellipse cx="0" cy="2" rx="9" ry="4" fill="#067978" opacity="0.12" />
                  <circle r="9" fill="#ffffff" stroke="#067978" strokeWidth="1.5" className="shadow-xs" />

                  <g transform={`rotate(${tangentAngle + 90})`}>
                    <path
                      d="M 12 2 L 14 7 L 22 10 L 22 12 L 14 13 L 14 18 L 17 20 L 17 21 L 12 20 L 7 21 L 7 20 L 10 18 L 10 13 L 2 12 L 2 10 L 10 7 Z"
                      transform="translate(-12, -12) scale(0.65)"
                      fill="#067978"
                    />
                  </g>
                </g>
              </svg>
            </div>

            <div className="w-full flex items-center justify-between text-[9.5px] font-mono text-slate-400 mt-1 px-3">
              <span>RUNWAY CLIMB</span>
              <span className="text-[#067978] font-normal">
                {progress === 100
                  ? 'TOUCHDOWN COMPLETED'
                  : progress > 0
                  ? `${Math.round(progress)}% OF JOURNEY`
                  : 'BOARDING GATE'}
              </span>
              <span>APPROACH DESCENT</span>
            </div>
          </div>

          {/* Arrival Node */}
          <div className="md:col-span-3 flex flex-col items-start md:items-end text-left md:text-right">
            <span className="text-[10px] uppercase font-medium text-slate-400 tracking-wider block mb-1">
              Arrival Airport
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-normal text-slate-600 order-2 md:order-1 truncate max-w-[130px]">
                {arrCity}
              </span>
              <span className="text-3xl font-normal text-slate-800 font-mono tracking-tight order-1 md:order-2">
                {arrIata}
              </span>
            </div>
            <p className="text-xs text-slate-400 font-normal truncate max-w-[200px] mt-0.5" title={arrAirport}>
              {arrAirport}
            </p>

            <div className="mt-3 flex items-baseline gap-2">
              {arrDelay.delay > 0 ? (
                <span className="text-[11px] font-normal text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200/60">
                  +{arrDelay.delay}m delay
                </span>
              ) : (
                <span className="text-[11px] font-normal text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200/60">
                  On Time
                </span>
              )}
              <span className="text-xl font-normal text-slate-800 font-mono">{arrTime}</span>
            </div>

            <span className="text-[11px] text-slate-500 font-normal mt-1">
              {flightData.arrival?.baggage ? `Belt ${flightData.arrival.baggage}` : 'Standard Claim'}
              {flightData.arrival?.terminal ? ` · Terminal ${flightData.arrival.terminal}` : ''}
            </span>
          </div>
        </div>
      </div>

      <div className="border-b border-dashed border-slate-200/90 my-4" />

      {/* Aviation Specifications & Flight Details Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 p-4 bg-slate-50/50 rounded-2xl border border-slate-100">
        <div>
          <span className="text-[10px] uppercase font-medium text-slate-400 tracking-wider block">Departure Gate</span>
          <span className="text-xs sm:text-sm font-normal text-slate-800 block mt-1">
            {flightData.departure?.terminal ? `Terminal ${flightData.departure.terminal}` : 'Terminal TBA'}
            {flightData.departure?.gate ? ` · Gate ${flightData.departure.gate}` : ''}
          </span>
          <span className="text-[11px] text-slate-400 font-normal block mt-0.5">
            {flightData.departure?.gate ? 'Assigned boarding gate' : 'Gate announced at airport'}
          </span>
        </div>

        <div>
          <span className="text-[10px] uppercase font-medium text-slate-400 tracking-wider block">
            Aircraft Equipment
          </span>
          <span className="text-xs sm:text-sm font-normal text-slate-800 block mt-1 truncate" title={aircraftName}>
            {aircraftName}
          </span>
          <span className="text-[11px] text-slate-400 font-normal block mt-0.5 font-mono">
            {aircraftReg ? `Reg: ${aircraftReg}` : 'Commercial Airliner'}
          </span>
        </div>

        <div>
          <span className="text-[10px] uppercase font-medium text-slate-400 tracking-wider block">
            Schedule Performance
          </span>
          <span
            className={`text-xs sm:text-sm font-normal block mt-1 ${
              depDelay.delay > 0 ? 'text-amber-700' : 'text-emerald-700'
            }`}
          >
            {depDelay.delay > 0 ? `${depDelay.delay} min Delay` : 'Operating On Time'}
          </span>
          <span className="text-[11px] text-slate-400 font-normal block mt-0.5">
            {depDelay.delay > 0 ? 'Pushed back departure' : 'Strictly on flight schedule'}
          </span>
        </div>

        <div>
          <span className="text-[10px] uppercase font-medium text-slate-400 tracking-wider block">
            Arrival Baggage Claim
          </span>
          <span className="text-xs sm:text-sm font-normal text-slate-800 block mt-1">
            {flightData.arrival?.baggage ? `Belt ${flightData.arrival.baggage}` : 'Standard Claim'}
            {flightData.arrival?.terminal ? ` · Terminal ${flightData.arrival.terminal}` : ''}
          </span>
          <span className="text-[11px] text-slate-400 font-normal block mt-0.5">
            {flightData.arrival?.baggage ? 'Designated luggage belt' : 'Follow airport arrival signs'}
          </span>
        </div>
      </div>

      {/* Live Telemetry Cockpit HUD Panel */}
      {flightData.live && (
        <div className="mt-4 p-4 rounded-2xl bg-slate-900 text-white text-xs">
          <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-800">
            <div className="flex items-center gap-1.5 text-slate-300">
              <Radio size={13} className="text-emerald-400 animate-pulse" />
              <span className="text-[10px] font-medium uppercase tracking-wider">Live In-Flight ADS-B Telemetry</span>
            </div>
            <span className="text-[10px] text-slate-400 font-mono">Satellite Feed</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <span className="text-[10px] text-slate-400 block font-normal">Altitude</span>
              <span className="text-sm font-mono text-emerald-400 font-normal">
                {Math.round(flightData.live.altitude || 0).toLocaleString()} m
              </span>
            </div>
            <div>
              <span className="text-[10px] text-slate-400 block font-normal">Ground Speed</span>
              <span className="text-sm font-mono text-white font-normal">
                {Math.round(flightData.live.speed_horizontal || 0)} km/h
              </span>
            </div>
            <div>
              <span className="text-[10px] text-slate-400 block font-normal">Heading Compass</span>
              <span className="text-sm font-mono text-white font-normal">
                {Math.round(flightData.live.direction || 0)}°
              </span>
            </div>
            <div>
              <span className="text-[10px] text-slate-400 block font-normal">Last Signal Ping</span>
              <span className="text-sm font-mono text-slate-300 font-normal">
                {flightData.live.updated
                  ? new Date(flightData.live.updated).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false
                    })
                  : 'Just now'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlightTrackingResults;
